import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { DEFAULT_PANEL_ROLES } from '@shared/auth/enums/app-role.enum';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { BaseListComponent } from '@shared/components/base-list/base-list.component';
import { ListService } from '@shared/modules/list/services/list.service';
import { ActivatedRoute } from '@angular/router';
import { Config } from '@shared/configs/config';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { Employee } from '@shared/models/employee.model';
import { ProjectTeamListConfig } from '@project-modules/project-team/shared/configs/project-team-list.config';
import { ProjectTeamFiltersComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-filters/project-team-filters.component';
import { ProjectTeamEmployeeCellComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-employee-cell/project-team-employee-cell.component';
import { ProjectTeamFunctionCellComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-function-cell/project-team-function-cell.component';
import { ProjectTeamPhoneCellComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-phone-cell/project-team-phone-cell.component';
import { ProjectTeamHourlyRateCellComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-hourly-rate-cell/project-team-hourly-rate-cell.component';
import { ProjectTeamRoleCellComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-role-cell/project-team-role-cell.component';
import { ProjectTeamRoleProjectCellComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-role-project-cell/project-team-role-project-cell.component';
import { ProjectTeamEmailCellComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-email-cell/project-team-email-cell.component';
import { ProjectTeamManageModalComponent } from '@project-modules/project-team/pages/project-team-list/components/project-team-manage-modal/project-team-manage-modal.component';
import { ProjectPreviewService } from '@project-modules/project-preview/services/project-preview.service';
import { WindowHelper } from '@shared/helpers/window.helper';
import { ContactManagePanelService } from '@shared/modules/contact-sidenav/services/contact-manage-panel.service';
import { tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectStoreService } from '@modules/projects/shared/services/project-store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'project-team-list',
  templateUrl: './project-team-list.component.html',
  styleUrls: ['./project-team-list.component.scss']
})
export class ProjectTeamListComponent extends BaseListComponent implements OnInit {
  private readonly projectStore = inject(ProjectStoreService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly defaultPanelRoles = DEFAULT_PANEL_ROLES;

  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  ProjectTeamFiltersComponent = ProjectTeamFiltersComponent;
  WindowHelper = WindowHelper;
  projectId: number;
  checkedTaskIds: number[] = [];

  project = computed<Project | null>(() => this.projectStore.project() || null);

  @ViewChild('projectTeamEmployeeCellComponent')
  public projectTeamEmployeeCellComponent: ProjectTeamEmployeeCellComponent;
  @ViewChild('projectTeamEmailCellComponent')
  public projectTeamEmailCellComponent: ProjectTeamEmailCellComponent;
  @ViewChild('projectTeamFunctionCellComponent')
  public projectTeamFunctionCellComponent: ProjectTeamFunctionCellComponent;
  @ViewChild('projectTeamPhoneCellComponent')
  public projectTeamPhoneCellComponent: ProjectTeamPhoneCellComponent;
  @ViewChild('projectTeamHourlyRateCellComponent')
  public projectTeamHourlyRateCellComponent: ProjectTeamHourlyRateCellComponent;
  @ViewChild('projectTeamRoleCellComponent')
  public projectTeamRoleCellComponent: ProjectTeamRoleCellComponent;
  @ViewChild('projectTeamRoleProjectCellComponent')
  public projectTeamRoleProjectCellComponent: ProjectTeamRoleProjectCellComponent;

  constructor(
    public changes: ChangeDetectorRef,
    public listService: ListService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public projectPreviewService: ProjectPreviewService,
    private contactPanelService: ContactManagePanelService
  ) {
    super(changes, listService);
    this.checkProjectId();
    this.configureHourlyRateColumn();
    this.initListConfig(ProjectTeamListConfig);
    this.setOnSideNavCloseListener();
  }

  checkProjectId() {
    if (this.route?.parent?.parent?.snapshot?.params?.projectId) {
      this.projectId = this.route?.parent?.parent?.snapshot?.params?.projectId;
      console.log('projectID', this.projectId);
      ProjectTeamListConfig.url = `${Config.API}/project/${this.projectId}/employee/search-advanced`;
    } else {
      ProjectTeamListConfig.url = `${Config.API}/project/[{projectId}]/employee/search-advanced`;
    }
  }

  ngOnInit(): void {
    this.initActionSubscription();
  }

  private initActionSubscription(): void {
    this.projectPreviewService.actionEvent$
      .pipe(
        tap(() => this.addNewTeamMember()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  configureHourlyRateColumn() {
    let isAddedHourlyRate = false;
    ProjectTeamListConfig.columns.map((c) => {
      if (c.prop === 'hourlyRate') {
        isAddedHourlyRate = true;
      }
    });

    if (!isAddedHourlyRate) {
      ProjectTeamListConfig.columns.splice(2, 0, {
        prop: 'hourlyRate',
        name: 'ProjectTeam.List.hourlyRate',
        componentRef: 'projectTeamHourlyRateCellComponent',
        sortField: 'a.hourlyRate',
        resizeable: false,
        hideOnMobile: true,
        width: 100,
        order: 3
      });
    }
  }

  addNewTeamMember() {
    const dialog = this.dialog.open(ProjectTeamManageModalComponent, {
      width: Config.DEFAULT_MODAL_WIDTH,
      autoFocus: false
    });
    dialog.componentInstance.projectId = this.projectPreviewService.project.id;
    this.sub.add(dialog.componentInstance.change.subscribe(() => this.listService.getRows()));
  }

  showToggleMenu(row: Employee) {
    return !isNotNullOrUndefined(row.deleted);
  }

  setOnSideNavCloseListener(): void {
    this.sub.add(this.contactPanelService.dataChanged$.subscribe(() => this.listService.getRows()));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.sub.unsubscribe();
  }
}
